// SPDX-License-Identifier: AGPL-3.0-or-later

import * as AccessibilityCommands from '@app/features/accessibility/commands/AccessibilityCommands';
import Accessibility, {
	ZOOM_LEVEL_MARKERS,
	ZOOM_LEVEL_MAX,
	ZOOM_LEVEL_MIN,
} from '@app/features/accessibility/state/Accessibility';
import {SettingsRow} from '@app/features/app/components/dialogs/shared/SettingsRow';
import {PRODUCT_NAME} from '@app/features/app/config/I18nDisplayConstants';
import {APP_ZOOM_LEVEL_DESCRIPTOR} from '@app/features/i18n/utils/CommonMessageDescriptors';
import Keybind from '@app/features/input/state/InputKeybind';
import {formatKeyCombo} from '@app/features/input/utils/KeybindUtils';
import {Slider} from '@app/features/ui/components/Slider';
import {canResetSliderValue, SliderResetIconButton} from '@app/features/ui/components/slider/SliderResetIconButton';
import {TextAaIcon} from '@app/features/ui/icons/Icons';
import {shouldWarnAboutFirefoxWebZoomShortcuts} from '@app/features/ui/utils/AppZoomKeybindUtils';
import {formatRoundedPercentage, roundPercentage} from '@app/features/ui/utils/PercentageFormatting';
import styles from '@app/features/user/components/modals/tabs/appearance_tab/MessagesTab.module.css';
import {msg, ph} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useEffect, useMemo} from 'react';

const CHAT_FONT_SIZE_DESCRIPTOR = msg({
	message: 'Chat font size',
	comment: 'Accessible label for the chat font size select.',
});
const RESET_FONT_SIZE_DESCRIPTOR = msg({
	message: 'Reset font size',
	comment: 'Button for the chat font size setting. Restores chat font size to the default.',
});
const PERCENT_DESCRIPTOR = msg({
	message: '{zoomPercent} percent',
	comment: 'Accessible value for the app zoom level slider.',
});
const RESET_ZOOM_DESCRIPTOR = msg({
	message: 'Reset zoom',
	comment: 'Label for controls that reset app zoom to 100%, including the app zoom slider button.',
});
const ADJUST_THE_OVERALL_ZOOM_LEVEL_OF_THE_APP_DESCRIPTOR = msg({
	message:
		'Adjust the overall zoom level of the app. Firefox keeps the standard browser zoom shortcuts, so use the slider here for {productName} zoom.',
	comment: 'Description for the app zoom setting in Firefox on the web. productName is the app name.',
});
const ADJUST_THE_OVERALL_ZOOM_LEVEL_OF_THE_APP_2_DESCRIPTOR = msg({
	message: 'Adjust the overall zoom level of the app. Use {zoomIn} / {zoomOut} to adjust quickly.',
	comment: 'Description for the app zoom setting. The placeholders are keyboard shortcuts.',
});
const DEFAULT_FONT_SIZE = 16;
const DEFAULT_ZOOM_PERCENT = 100;
export const FONT_SIZE_OPTIONS = [12, 14, 15, 16, 18, 20, 24] as const;

export const getNearestFontSize = (value: number): number => {
	let nearest: number = FONT_SIZE_OPTIONS[0];
	let nearestDistance = Number.POSITIVE_INFINITY;
	for (const option of FONT_SIZE_OPTIONS) {
		const distance = Math.abs(option - value);
		if (distance < nearestDistance) {
			nearest = option;
			nearestDistance = distance;
		}
	}
	return nearest;
};

const formatFontSize = (value: number): string => `${value}px`;

export function canResetFontSize(): boolean {
	return canResetSliderValue(Accessibility.fontSize, DEFAULT_FONT_SIZE);
}

export const FontSizeResetAction: React.FC = observer(() => {
	const {i18n} = useLingui();
	return (
		<SliderResetIconButton
			canReset={true}
			onReset={() => AccessibilityCommands.update({fontSize: DEFAULT_FONT_SIZE})}
			ariaLabel={i18n._(RESET_FONT_SIZE_DESCRIPTOR)}
			dataFlx="user.appearance-tab.scaling-tab.reset-button.font-size"
			data-flx="user.appearance-tab.scaling-tab.font-size-reset-action.slider-reset-icon-button"
		/>
	);
});

/**
 * KONVO round 6: chat font size as a grouped row with the kit slider. The slider moves in
 * even steps over the allowed sizes (12, 14, 15, 16, 18, 20, 24 px) and writes the same setting.
 */
export const FontSizeTabContent: React.FC<{id?: string}> = observer(({id}) => {
	const {i18n} = useLingui();
	const fontSize = Accessibility.fontSize;
	const selectedFontSize = getNearestFontSize(fontSize);
	const selectedIndex = Math.max(0, FONT_SIZE_OPTIONS.indexOf(selectedFontSize as (typeof FONT_SIZE_OPTIONS)[number]));
	const defaultIndex = FONT_SIZE_OPTIONS.indexOf(DEFAULT_FONT_SIZE as (typeof FONT_SIZE_OPTIONS)[number]);
	const sizeAt = (index: number): number =>
		FONT_SIZE_OPTIONS[Math.min(FONT_SIZE_OPTIONS.length - 1, Math.max(0, Math.round(index)))];
	useEffect(() => {
		if (fontSize !== selectedFontSize) AccessibilityCommands.update({fontSize: selectedFontSize});
	}, [fontSize, selectedFontSize]);
	return (
		<SettingsRow
			id={id}
			icon={<TextAaIcon size={20} />}
			title={i18n._(CHAT_FONT_SIZE_DESCRIPTOR)}
			description={formatFontSize(selectedFontSize)}
			control={({labelId}) => (
				<span className={styles.rowSlider}>
					{canResetFontSize() ? (
						<FontSizeResetAction data-flx="user.appearance-tab.scaling-tab.font-size-tab-content.font-size-reset-action" />
					) : null}
					<Slider
						value={selectedIndex}
						defaultValue={selectedIndex}
						factoryDefaultValue={defaultIndex}
						minValue={0}
						maxValue={FONT_SIZE_OPTIONS.length - 1}
						step={1}
						ariaLabelledBy={labelId}
						ariaValueText={formatFontSize(selectedFontSize)}
						onValueRender={(index) => formatFontSize(sizeAt(index))}
						onValueChange={(index) => {
							const next = sizeAt(index);
							if (next !== Accessibility.fontSize) AccessibilityCommands.update({fontSize: next});
						}}
						data-flx="user.appearance-tab.scaling-tab.font-size-tab-content.slider"
					/>
					<span className={styles.rowValue} aria-hidden="true">
						{selectedFontSize}
					</span>
				</span>
			)}
			data-flx="user.appearance-tab.scaling-tab.font-size-tab-content.row"
		/>
	);
});

export function canResetAppZoomLevel(): boolean {
	const zoomPercent = roundPercentage(Accessibility.zoomLevel * 100);
	return canResetSliderValue(zoomPercent, DEFAULT_ZOOM_PERCENT);
}

export const AppZoomLevelResetAction: React.FC = observer(() => {
	const {i18n} = useLingui();
	return (
		<SliderResetIconButton
			canReset={true}
			onReset={() => AccessibilityCommands.update({zoomLevel: 1})}
			ariaLabel={i18n._(RESET_ZOOM_DESCRIPTOR)}
			dataFlx="user.appearance-tab.scaling-tab.reset-button.app-zoom-level"
			data-flx="user.appearance-tab.scaling-tab.app-zoom-level-reset-action.slider-reset-icon-button"
		/>
	);
});

export const AppZoomLevelTabContent: React.FC = observer(() => {
	const zoomLevel = Accessibility.zoomLevel;
	const zoomPercent = roundPercentage(zoomLevel * 100);
	const markers = ZOOM_LEVEL_MARKERS.map((step) => roundPercentage(step * 100));
	const {i18n} = useLingui();
	return (
		<Slider
			defaultValue={zoomPercent}
			factoryDefaultValue={DEFAULT_ZOOM_PERCENT}
			minValue={Math.round(ZOOM_LEVEL_MIN * 100)}
			maxValue={Math.round(ZOOM_LEVEL_MAX * 100)}
			ariaLabel={i18n._(APP_ZOOM_LEVEL_DESCRIPTOR)}
			ariaValueText={i18n._(PERCENT_DESCRIPTOR, {zoomPercent})}
			step={1}
			markers={markers}
			stickToMarkers={false}
			asValueChanges={() => {}}
			onValueChange={(value) => AccessibilityCommands.update({zoomLevel: value / 100})}
			onMarkerRender={formatRoundedPercentage}
			onValueRender={(value) => <Trans>{ph({zoomPercent: roundPercentage(value)})}%</Trans>}
			data-flx="user.appearance-tab.scaling-tab.app-zoom-level-tab-content.slider"
		/>
	);
});

export function useAppZoomLevelDescription(): string {
	const {i18n} = useLingui();
	return useMemo(() => {
		if (shouldWarnAboutFirefoxWebZoomShortcuts()) {
			return i18n._(ADJUST_THE_OVERALL_ZOOM_LEVEL_OF_THE_APP_DESCRIPTOR, {productName: PRODUCT_NAME});
		}
		const zoomIn = formatKeyCombo(Keybind.getByAction('system_zoom_in').combo);
		const zoomOut = formatKeyCombo(Keybind.getByAction('system_zoom_out').combo);
		return i18n._(ADJUST_THE_OVERALL_ZOOM_LEVEL_OF_THE_APP_2_DESCRIPTOR, {zoomIn, zoomOut});
	}, [i18n.locale]);
}
